import { Outlet, createRootRouteWithContext, Link } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { TooltipProvider } from '@/components/ui/controls'
import { Button } from '@/components/ui/button'
import { ErrorState } from '@/components/states'

export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFound,
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-[640px] p-8">
      <ErrorState error={error} onRetry={reset} />
    </div>
  ),
})

function Root() {
  return (
    <TooltipProvider>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      <Outlet />
      <Toaster position="bottom-right" richColors closeButton toastOptions={{ style: { fontSize: 12.5 } }} />
    </TooltipProvider>
  )
}

/** SCR-112: page not found (also rendered inside the app shell for unknown app paths). */
export function NotFound() {
  return (
    <div className="flex min-h-[60dvh] items-center justify-center p-6">
      <div className="max-w-[420px] text-center">
        <div className="text-[42px] font-black tracking-tight text-accent">404</div>
        <h1 className="mt-1 text-[18px] font-bold text-ink">This page doesn’t exist</h1>
        <p className="mt-2 text-[12.5px] leading-relaxed text-muted">The link may be old or mistyped, or the page may have moved. Nothing has been lost.</p>
        <div className="mt-5 flex justify-center gap-2">
          <Button asChild variant="primary">
            <Link to="/">Go to ESM home</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/help">Get help</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
